import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { usePositionContext } from './PositionContext';
import { useAccount } from './AccountContext';
import { useAccountType } from './AccountTypeContext';
import { aiTradingService, AITradingServiceConfig } from '../services/aiTradingService';

export interface AIStrategyConfig {
  strategy: string;
  config: any;
  isRunning: boolean;
  signal: 'buy' | 'sell' | null;
  startedTime?: number | null;
  showMonitoringMessage?: boolean;
  lastSignalTime?: number | null;
  aiPositions?: string[]; // Array of position IDs created by AI
}

interface AITradingContextType {
  assetStrategies: { [symbol: string]: AIStrategyConfig };
  updateStrategy: (symbol: string, updates: Partial<AIStrategyConfig>) => void;
  startStrategy: (symbol: string) => void;
  stopStrategy: (symbol: string) => void;
  updateSignal: (symbol: string, signal: 'buy' | 'sell' | null) => void;
  updateConfig: (symbol: string, config: any) => void;
  getStrategy: (symbol: string) => AIStrategyConfig;
  isStrategyRunning: (symbol: string) => boolean;
  executeAITrade: (symbol: string, signal: 'buy' | 'sell', currentPrice: string) => void;
  closeAllAIPositions: (symbol: string) => void;
  updatePriceForAI: (symbol: string, price: string) => void;
  registerScrollCallback: (callback: () => void) => void;
  unregisterScrollCallback: () => void;
}

const AITradingContext = createContext<AITradingContextType | undefined>(undefined);

export const useAITrading = () => {
  const context = useContext(AITradingContext);
  if (context === undefined) {
    throw new Error('useAITrading must be used within an AITradingProvider');
  }
  return context;
};

interface AITradingProviderProps {
  children: ReactNode;
}

export const AITradingProvider: React.FC<AITradingProviderProps> = ({ children }) => {
  const [assetStrategies, setAssetStrategies] = useState<{ [symbol: string]: AIStrategyConfig }>({});
  const [scrollCallback, setScrollCallback] = useState<(() => void) | null>(null);
  const { addOpenPosition, closePosition, openPositions } = usePositionContext();
  const { accountBalance } = useAccount();
  const { accountType } = useAccountType();

  // Initialize AI trading service
  useEffect(() => {
    const config: AITradingServiceConfig = {
      onSignal: (symbol: string, signal: 'buy' | 'sell', price: string) => {
        updateSignal(symbol, signal);
        executeAITrade(symbol, signal, price);
      },
      onPriceUpdate: (symbol: string, price: string) => {
        // Price updates are handled by the service itself
      }
    };

    aiTradingService.setConfig(config);

    // Cleanup on unmount
    return () => {
      aiTradingService.cleanup();
    };
  }, []); // Empty dependency array - we'll update the config when functions change

  const updateStrategy = useCallback((symbol: string, updates: Partial<AIStrategyConfig>) => {
    setAssetStrategies(prev => {
      const currentConfig = prev[symbol] || {
        strategy: '',
        config: { period: 10, multiplier: 3.0, tradeAmountPercent: 10 },
        isRunning: false,
        signal: null,
        startedTime: null,
        showMonitoringMessage: false,
        lastSignalTime: null,
        aiPositions: []
      };

      return {
        ...prev,
        [symbol]: {
          ...currentConfig,
          ...updates
        }
      };
    });
  }, []);

  const stopStrategy = useCallback((symbol: string) => {
    // Close all AI positions for this symbol before stopping
    const strategy = assetStrategies[symbol];
    if (strategy?.aiPositions) {
      strategy.aiPositions.forEach(positionId => {
        const position = openPositions.find(p => p.id === positionId);
        if (position) {
          closePosition(positionId, position.current);
        }
      });
    }

    // Remove symbol from global AI trading service
    aiTradingService.removeSymbol(symbol);

    updateStrategy(symbol, {
      isRunning: false,
      startedTime: null,
      showMonitoringMessage: false,
      signal: null,
      lastSignalTime: null,
      aiPositions: []
    });
  }, [updateStrategy, assetStrategies, openPositions, closePosition]);

  const updateSignal = useCallback((symbol: string, signal: 'buy' | 'sell' | null) => {
    updateStrategy(symbol, { 
      signal,
      lastSignalTime: signal ? Date.now() : null
    });
  }, [updateStrategy]);

  const updateConfig = useCallback((symbol: string, config: any) => {
    updateStrategy(symbol, { config });
  }, [updateStrategy]);

  const getStrategy = useCallback((symbol: string): AIStrategyConfig => {
    return assetStrategies[symbol] || {
      strategy: '',
      config: { period: 10, multiplier: 3.0, tradeAmountPercent: 10 },
      isRunning: false,
      signal: null,
      startedTime: null,
      showMonitoringMessage: false,
      lastSignalTime: null,
      aiPositions: []
    };
  }, [assetStrategies]);

  const isStrategyRunning = useCallback((symbol: string): boolean => {
    return getStrategy(symbol).isRunning;
  }, [getStrategy]);

  const startStrategy = useCallback((symbol: string) => {
    const strategy = getStrategy(symbol);
    
    // Don't start if no strategy is selected
    if (!strategy.strategy) {
      console.log('Cannot start AI trading: No strategy selected for', symbol);
      return;
    }
    
    updateStrategy(symbol, {
      isRunning: true,
      signal: null,
      startedTime: Date.now(),
      showMonitoringMessage: false,
      lastSignalTime: null,
      aiPositions: []
    });

    // Add symbol to global AI trading service with strategy and config
    aiTradingService.addSymbol(symbol, strategy.strategy, strategy.config);

    // Show monitoring message after 2 seconds
    setTimeout(() => {
      updateStrategy(symbol, { showMonitoringMessage: true });
    }, 2000);
  }, [updateStrategy, getStrategy]);

  // Execute AI trade based on signal
  const executeAITrade = useCallback((symbol: string, signal: 'buy' | 'sell', currentPrice: string) => {
    if (!currentPrice || !accountBalance) {
      return;
    }

    const strategy = getStrategy(symbol);
    if (!strategy.isRunning) {
      return;
    }

    const price = parseFloat(currentPrice);
    const leverage = strategy.config?.leverage || 100; // Use configured leverage or default to 100
    
    // Calculate trade amount based on percentage of balance
    const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
    const tradeAmountUSD = (currentBalance * (strategy.config?.tradeAmountPercent || 10)) / 100;
    const amount = tradeAmountUSD / price; // Convert USD to asset amount
    
    // Check if we have sufficient balance
    if (tradeAmountUSD > currentBalance) {
      return;
    }

    // Only close existing AI positions if the signal has changed
    const lastSignal = strategy.signal;
    if (lastSignal && lastSignal !== signal && strategy.aiPositions && strategy.aiPositions.length > 0) {
      strategy.aiPositions.forEach(positionId => {
        const position = openPositions.find(p => p.id === positionId);
        if (position) {
          closePosition(positionId, currentPrice);
        }
      });
      // Clear the positions array after closing
      updateStrategy(symbol, { aiPositions: [] });
    }

    // Create new AI position
    if (addOpenPosition) {
      
      const positionData = {
        symbol: symbol,
        type: (signal === 'buy' ? 'Buy' : 'Sell') as 'Buy' | 'Sell',
        size: amount.toFixed(6), // More precision for crypto amounts
        entry: price.toString(),
        current: price.toString(),
        pnl: '$0.00',
        pnlPercent: '0.00%',
        leverage: leverage,
        isAI: true, // Mark as AI position
      };
      
      const newPosition = addOpenPosition(positionData);
      
      // Track the new AI position ID
      if (newPosition) {
        const currentPositions = strategy.aiPositions || [];
        updateStrategy(symbol, { 
          aiPositions: [...currentPositions, newPosition.id]
        });
        
        // Call scroll callback if registered
        if (scrollCallback) {
          scrollCallback();
        }
      }
    }
  }, [accountBalance, accountType, getStrategy, addOpenPosition, closePosition, openPositions, updateStrategy]);

  // Update AI service config when functions change
  useEffect(() => {
    const config: AITradingServiceConfig = {
      onSignal: (symbol: string, signal: 'buy' | 'sell', price: string) => {
        updateSignal(symbol, signal);
        executeAITrade(symbol, signal, price);
      },
      onPriceUpdate: (symbol: string, price: string) => {
        // Price updates are handled by the service itself
      }
    };

    aiTradingService.setConfig(config);
  }, [updateSignal, executeAITrade]);

  // Close all AI positions for a specific symbol
  const closeAllAIPositions = useCallback((symbol: string) => {
    const strategy = getStrategy(symbol);
    if (strategy.aiPositions) {
      strategy.aiPositions.forEach(positionId => {
        const position = openPositions.find(p => p.id === positionId);
        if (position) {
          closePosition(positionId, position.current);
        }
      });
    }
    updateStrategy(symbol, { aiPositions: [] });
  }, [getStrategy, openPositions, closePosition, updateStrategy]);

  // Update price data for AI trading service
  const updatePriceForAI = useCallback((symbol: string, price: string) => {
    // Only update price if AI trading is active for this symbol
    if (aiTradingService.isSymbolActive(symbol)) {
      aiTradingService.updateSymbolPrice(symbol, price);
    }
  }, []);

  // Track AI positions by checking openPositions for AI-flagged positions
  useEffect(() => {
    Object.keys(assetStrategies).forEach(symbol => {
      const strategy = assetStrategies[symbol];
      if (strategy.isRunning) {
        const aiPositions = openPositions
          .filter(p => p.symbol === symbol && p.isAI)
          .map(p => p.id);
        
        if (JSON.stringify(aiPositions) !== JSON.stringify(strategy.aiPositions)) {
          updateStrategy(symbol, { aiPositions });
        }
      }
    });
  }, [openPositions, assetStrategies, updateStrategy]);

  // Scroll callback functions
  const registerScrollCallback = useCallback((callback: () => void) => {
    setScrollCallback(() => callback);
  }, []);

  const unregisterScrollCallback = useCallback(() => {
    setScrollCallback(null);
  }, []);

  const value: AITradingContextType = {
    assetStrategies,
    updateStrategy,
    startStrategy,
    stopStrategy,
    updateSignal,
    updateConfig,
    getStrategy,
    isStrategyRunning,
    executeAITrade,
    closeAllAIPositions,
    updatePriceForAI,
    registerScrollCallback,
    unregisterScrollCallback
  };

  return (
    <AITradingContext.Provider value={value}>
      {children}
    </AITradingContext.Provider>
  );
};
